import { useMemo } from 'react';

import {
  type Dimension1,
  POSTING_GET_DATA,
  type PostingsDataQuery,
} from '@revelio/data-access';

import { useSelectionListState } from '../../../../shared/selection-lists/hooks/useSelectionListState';
import { useEffectiveFilters } from '../../filters/hooks/useEffectiveFilters';
import { FilterId, type PrimaryFilterId } from '../../filters/types/filters';
import { getDim1 } from '../../filters/utils/getDim1';
import { getPostingFilters } from '../../filters/utils/getPostingFilters';
import { useChart } from '../Chart/ChartProvider';
import type { MapPoint } from '../Chart/Map/types';
import { determineLocationType } from '../Chart/Map/utils/location-type-utils';
import type { PostingMetricValues } from '../PageConfigProvider/types/metric';
import { type DataProviderChildren, DataProviderQueryStatus } from './types';
import {
  extractGranularitiesWithIds,
  useCoordinateResolverForMixedGranularities,
} from './utils/coordinate-resolver';
import {
  combineQueryStatus,
  useUrqlQueryWithStatus,
} from './utils/data-provider-results-utils';

type PostingMapDataProviderProps = {
  primaryDimension: PrimaryFilterId;
  metric: PostingMetricValues;
  exchangeRate?: number;
  children: DataProviderChildren<{ points: MapPoint[] } | undefined>;
};

const mapMetricToField = (metric: PostingMetricValues) => {
  const mapping: Partial<
    Record<
      PostingMetricValues,
      'salary' | 'time_to_fill' | 'active' | 'new' | 'removed'
    >
  > = {
    posting_salary: 'salary',
    time_to_fill: 'time_to_fill',
    postings_active: 'active',
    postings_volume: 'active',
    postings_new: 'new',
    postings_removed: 'removed',
  };
  return mapping[metric] ?? 'active';
};

export const PostingMapDataProvider = ({
  primaryDimension,
  metric,
  exchangeRate,
  children,
}: PostingMapDataProviderProps) => {
  const { item: _item } = useChart();
  const effectiveFilters = useEffectiveFilters({ viewGranularity: 'week' });
  const { selectionLists } = useSelectionListState();
  const postingFilters = getPostingFilters(primaryDimension, effectiveFilters);

  const isGeographyView = !!getDim1(FilterId.GEOGRAPHY);

  // Create filter configurations for dual requests when countries are selected
  const { countryFilters, msaFilters, needsDualRequest } = useMemo(() => {
    if (!selectionLists) {
      return {
        countryFilters: {
          filters: postingFilters,
          dim1: getDim1(FilterId.GEOGRAPHY) as Dimension1,
        },
        msaFilters: null,
        needsDualRequest: false,
      };
    }

    const { region, country, msa } = postingFilters;

    // If no geo filters are selected, use all countries
    if (!region?.length && !country?.length && !msa?.length) {
      const enrichedCountry =
        selectionLists.country?.map((c) => Number(c.id)) || [];
      return {
        countryFilters: {
          filters: {
            ...postingFilters,
            region: [],
            country: enrichedCountry,
            msa: [],
          },
          dim1: getDim1(FilterId.GEOGRAPHY) as Dimension1,
        },
        msaFilters: null,
        needsDualRequest: false,
      };
    }
    // If only countries are selected, we need dual requests
    else if (country?.length && !msa?.length) {
      const enrichedMsa =
        selectionLists.msa
          ?.filter((m) => country.includes(Number(m.country)))
          .map((m) => Number(m.id)) || [];

      return {
        countryFilters: {
          filters: {
            ...postingFilters,
            region: [],
            country: country,
            msa: [],
          },
          dim1: getDim1(FilterId.GEOGRAPHY) as Dimension1,
        },
        msaFilters: {
          filters: {
            ...postingFilters,
            region: [],
            country: [],
            msa: enrichedMsa,
          },
          dim1: getDim1(FilterId.GEOGRAPHY) as Dimension1,
        },
        needsDualRequest: true,
      };
    }
    // For other cases (regions or MSAs selected), use single request
    else {
      return {
        countryFilters: {
          filters: postingFilters,
          dim1: getDim1(FilterId.GEOGRAPHY) as Dimension1,
        },
        msaFilters: null,
        needsDualRequest: false,
      };
    }
  }, [postingFilters, selectionLists]);

  // First request (always made)
  const [{ data: countryData, status: countryStatus }] =
    useUrqlQueryWithStatus<PostingsDataQuery>({
      query: POSTING_GET_DATA,
      variables: countryFilters,
      pause: !isGeographyView,
    });

  // Second request (only when dual request is needed)
  const [{ data: msaData, status: msaStatus }] =
    useUrqlQueryWithStatus<PostingsDataQuery>({
      query: POSTING_GET_DATA,
      variables: msaFilters || undefined,
      pause: !needsDualRequest || !msaFilters || !isGeographyView,
    });

  // Combine status and data from both requests
  const status = needsDualRequest
    ? combineQueryStatus([countryStatus, msaStatus])
    : countryStatus;

  const data = useMemo(() => {
    if (!needsDualRequest) {
      return countryData;
    }

    // Merge data from both requests
    if (!countryData?.posting || !msaData?.posting) {
      return countryData || msaData;
    }

    // Combine entities from both requests
    const combinedEntities = [...countryData.posting, ...msaData.posting];

    return {
      ...countryData,
      posting: combinedEntities,
    };
  }, [countryData, msaData, needsDualRequest]);

  const entities = useMemo(() => data?.posting ?? [], [data?.posting]);

  const metricField = mapMetricToField(metric);

  const geoData = useMemo(() => {
    return entities
      .map((entity) => {
        const id = Number(entity?.metadata?.id ?? 0);
        const label =
          entity?.metadata?.shortName || entity?.metadata?.longName || '';
        const last = entity?.category?.[entity?.category.length - 1];
        const rawValue = last?.metrics?.[metricField] ?? null;
        if (!id || rawValue == null) return null;
        const value =
          metric === 'posting_salary' && exchangeRate
            ? Number(rawValue) * exchangeRate
            : Number(rawValue);
        return { id, label, value };
      })
      .filter(
        (d): d is { id: number; label: string; value: number } => d !== null
      );
  }, [entities, metricField, metric, exchangeRate]);

  const granularitiesWithIds = useMemo(() => {
    return extractGranularitiesWithIds(geoData);
  }, [geoData]);

  const { data: coords, loading: coordsLoading } =
    useCoordinateResolverForMixedGranularities(granularitiesWithIds);

  const points = useMemo((): MapPoint[] => {
    if (!coords.length || !geoData.length) return [];
    const coordMap = new Map(coords.map((c) => [c.id, c] as const));
    return geoData
      .map((d) => {
        const c = coordMap.get(d.id);
        if (!c) return null;
        const locationType = determineLocationType(c.name, c.granularity);
        return {
          id: String(d.id),
          label: d.label || c.name,
          lat: c.lat,
          lon: c.lon,
          value: d.value,
          circleRadius: 0,
          locationType,
        } as MapPoint;
      })
      .filter((p): p is MapPoint => p !== null);
  }, [coords, geoData]);

  const finalStatus = coordsLoading
    ? DataProviderQueryStatus.Fetching
    : (status ?? DataProviderQueryStatus.Idle);

  return children({ data: { points }, status: finalStatus });
};
